import React from 'react';
import {
  Dialog,
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Avatar,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, CheckCircle, Cancel as CancelIcon } from '@mui/icons-material';
import { PictureAsPdf, KeyboardArrowRight, DeleteOutline, PhoneIphone } from '@mui/icons-material';
import contractorService, { Contractor } from '../../services/contractorService';
import { getAllProjects, Project, getInitials } from '../../services/projectService';
import { useAuth } from '../../contexts/AuthContext';
import { listUsersPublicForBusinessForms } from '../../services/userService';
import { updateTransferInvoice, createTransferInvoice } from '../../services/invoicesService';
import { useNotification } from '../layouts/Layout';
import api from '../../services/api';
import { normalizeUnit, ensureUnitOptions } from '../../utils/units';

export type PaymentMethod = 'Przelew' | 'Proforma' | 'Gotówka' | 'Karta' | 'Zaliczka (przelew)';
export type DocumentKind = 'vat' | 'advance' | 'proforma' | 'correction';

export interface EditInvoiceInitialData {
  id: number | string;
  document_type?: PaymentMethod;
  document_kind?: DocumentKind;
  document_date: string;
  received_date: string;
  project_id: string;
  invoice_number?: string;
  contractor_id?: string | number | null;
  contractor_name: string;
  net_amount: number;
  vat_rate: '0' | '8' | '23' | 'zw' | 'różne';
  gross_amount: number;
  calc_mode?: 'net' | 'gross' | 'none';
  due_date: string;
  priority: 'zwykły' | 'pilny';
  google_drive_file_id?: string | null;
  google_drive_file_url?: string | null;
  ksef_invoice_id?: string | null;
  ksef_status?: 'Zatwierdzona' | 'Odrzucona' | 'Do weryfikacji' | null;
  payment_method?: string | null;
  payment_due_date?: string | null;
  account_number?: string | null;
  nip?: string | null;
  contractor_address?: string | null;
  contractor_postal_city?: string | null;
  ksef_number?: string | null;
  warehouse?: string | null;
  /** Osoba, która wprowadziła fakturę (kolumna „W" w tabeli). */
  created_by_user_id?: string | null;
  created_by_name?: string | null;
  created_by_avatar_url?: string | null;
}

interface InvoiceLineItem {
  id: number;
  name: string;
  quantity: number | '';
  unit: string;
  unitNetPrice: number | '';
  unitGrossPrice: number | '';
  vatRate: number | string;
  netValue: number | '';
  grossValue: number | '';
}

interface EditTransferInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  invoice: EditInvoiceInitialData;
  onSaved?: () => void;
  highlightWarehouse?: boolean;
}

const round2 = (value: number): number => Math.round(value * 100) / 100;

const EditTransferInvoiceDialog: React.FC<EditTransferInvoiceDialogProps> = ({ open, onClose, invoice, onSaved, highlightWarehouse }) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [projects, setProjects] = React.useState<Array<{ project_id: string; project_name: string }>>([]);
  const STATIC_PROJECTS: Array<{ project_id: string; project_name: string }> = [
    { project_id: 'S14', project_name: 'SERWIS GWARANCYJNY: USŁUGI' },
    { project_id: 'S13', project_name: 'SERWIS GWARANCYJNY: MATERIAŁY' },
    { project_id: 'S12', project_name: 'LEASINGI' },
    { project_id: 'S11', project_name: 'OBSŁUGA PRAWNA, KSIĘGOWA, DORADCZA' },
    { project_id: 'S10', project_name: 'USŁUGI TELEINFORMATYCZNE' },
    { project_id: 'S9',  project_name: 'NAJEM MIESZKAN PRACOWNICZYCH' },
    { project_id: 'S8',  project_name: 'NAJEM POWIERZCHNI BIUROWEJ' },
    { project_id: 'S7',  project_name: 'PRACOWNICY B2B' },
    { project_id: 'S6',  project_name: 'ZARZĄD' },
    { project_id: 'S5',  project_name: 'USŁUGI WŁASNE' },
    { project_id: 'S4',  project_name: 'MATERIAŁY WŁASNE' },
    { project_id: 'S3',  project_name: 'DROBNE WYPOSAŻENIE - do 1500 zł' },
    { project_id: 'S2',  project_name: 'WYPOSAŻENIE - do 3.000 zł' },
    { project_id: 'S1',  project_name: 'ŚRODKI TRWAŁE - powyżej 3.000 zł' },
    { project_id: 'R11', project_name: 'PODATEK CIT' },
    { project_id: 'R10', project_name: 'PODATEK VAT' },
    { project_id: 'R9',  project_name: 'ZUS' },
    { project_id: 'R8',  project_name: 'PODATKI - PIT' },
    { project_id: 'R7',  project_name: 'POZOSTAŁE NIGDZIE NIE UJĘTE' },
    { project_id: 'R6',  project_name: 'OBSŁUGA BANKOWA' },
    { project_id: 'R5',  project_name: 'UBEZPIECZENIA I POLISY' },
    { project_id: 'R4',  project_name: 'PRACOWNICY UoP, UD, UZ' },
    { project_id: 'R3',  project_name: 'ZABEZPIECZENIE KONTRAKTÓW' },
    { project_id: 'R2',  project_name: 'KOSZTY KREDYTÓW' },
    { project_id: 'R1',  project_name: 'AMORTYZACJA' },
    { project_id: '.UN', project_name: 'USŁUGI NOCLEGOWE' },
    { project_id: '.UG', project_name: 'USŁUGI GASTRONOMICZNE' },
    { project_id: '.P2', project_name: 'POJAZDY - 50% ODLICZENIA VAT' },
    { project_id: '.P1', project_name: 'POJAZDY - 100% ODLICZENIA VAT' },
  ];
  const [loadingProjects, setLoadingProjects] = React.useState(false);

  const [contractorSearchQuery, setContractorSearchQuery] = React.useState('');
  const [contractorOptions, setContractorOptions] = React.useState<Contractor[]>([]);
  const [loadingContractors, setLoadingContractors] = React.useState(false);

  // Lista użytkowników do dropdownu „Wprowadził" (kolumna „W" w tabeli faktur).
  // Pobierana z `/users/all?forBusiness=1` – bez pracowników terenowych.
  const [enteredByUsers, setEnteredByUsers] = React.useState<Array<{ user_id: string; display_name?: string; email: string; avatar_url?: string }>>([]);

  const [fileUrl, setFileUrl] = React.useState<string | null>(null);
  const [file, setFile] = React.useState<File | null>(null);

  const [form, setForm] = React.useState({
    invoiceDate: invoice.document_date || '',
    receivedDate: invoice.received_date || '',
    projectId: invoice.project_id || '',
    invoiceNumber: invoice.invoice_number || '',
    contractorId: invoice.contractor_id ? String(invoice.contractor_id) : undefined,
    contractorName: invoice.contractor_name || '',
    netAmount: Number(invoice.net_amount || 0) as number | '',
    vatRate: (invoice.vat_rate === 'zw' ? 'zw' : (invoice.vat_rate === 'różne' ? 'różne' : Number(invoice.vat_rate))) as 0 | 8 | 23 | 'zw' | 'różne',
    grossAmount: Number(invoice.gross_amount || 0) as number | '',
    dueDate: invoice.due_date || '',
    priority: invoice.priority || 'zwykły',
    paymentMethod: (invoice.document_type || 'Przelew') as PaymentMethod,
    documentKind: (invoice.document_kind || 'vat') as DocumentKind,
    calcMode: (invoice.calc_mode || 'net') as 'net' | 'gross' | 'none',
    ksefStatus: (invoice.ksef_status === 'Zatwierdzona' || invoice.ksef_status === 'Odrzucona' ? invoice.ksef_status : '') as 'Zatwierdzona' | 'Odrzucona' | '',
    bankAccount: invoice.account_number || '',
    nip: invoice.nip || '',
    contractorAddress: invoice.contractor_address || '',
    contractorPostalCity: invoice.contractor_postal_city || '',
    warehouse: invoice.warehouse || '',
    ksefNumber: invoice.ksef_number || '',
    // „Wprowadził" – domyślnie osoba z bazy (jeśli edytujemy istniejącą fakturę),
    // a w nowych pozycjach (np. KSeF -> import) aktualnie zalogowany użytkownik.
    enteredByUserId: (invoice.created_by_user_id || (user as any)?.user_id || '') as string,
  });

  const [formTab, setFormTab] = React.useState(0); // 0 = Informacje, 1 = Wartości i pozycje
  const [withLineItems, setWithLineItems] = React.useState(false); // false = bez pozycji, true = z pozycjami
  const [lineItems, setLineItems] = React.useState<InvoiceLineItem[]>([
    { id: 1, name: '', quantity: '', unit: 'szt.', unitNetPrice: '', unitGrossPrice: '', vatRate: 23, netValue: '', grossValue: '' }
  ]);
  const [loadingItems, setLoadingItems] = React.useState(false);
  const [focusedPriceField, setFocusedPriceField] = React.useState<string | null>(null);
  const fmtPrice = (val: any, fieldKey?: string) => {
    if (fieldKey && focusedPriceField === fieldKey) return val;
    if (typeof val === 'number') return val.toFixed(2);
    return (val === '' || val === undefined || val === null) ? '' : val;
  };

  // Ref pilnujący, czy użytkownik realnie edytuje pozycje. Używany przez
  // efekt sumujący na dole, żeby NIE nadpisywał wartości nagłówka po samym
  // wczytaniu pozycji z KSeF (zob. komentarz przy efekcie).
  const userEditedItemsRef = React.useRef(false);

  const emptyLineItem = (): InvoiceLineItem => ({ id: Date.now(), name: '', quantity: '', unit: 'szt.', unitNetPrice: '', unitGrossPrice: '', vatRate: 23, netValue: '', grossValue: '' });
  const addLineItem = () => {
    userEditedItemsRef.current = true;
    setLineItems(prev => [...prev, emptyLineItem()]);
  };
  const removeLineItem = (id: number) => {
    userEditedItemsRef.current = true;
    setLineItems(prev => prev.length > 1 ? prev.filter(i => i.id !== id) : prev);
  };
  const updateLineItem = (id: number, field: keyof InvoiceLineItem, value: any) => {
    userEditedItemsRef.current = true;
    setLineItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: value };
      const qty = typeof updated.quantity === 'number' ? updated.quantity : 0;
      const rate = typeof updated.vatRate === 'number' ? updated.vatRate : 0;
      if (form.calcMode === 'gross') {
        const gPrice = typeof updated.unitGrossPrice === 'number' ? updated.unitGrossPrice : 0;
        if (qty > 0 && gPrice > 0) {
          updated.grossValue = round2(qty * gPrice);
          updated.netValue = rate > 0 ? round2(updated.grossValue / (1 + rate / 100)) : updated.grossValue;
          updated.unitNetPrice = rate > 0 ? round2(gPrice / (1 + rate / 100)) : gPrice;
        } else { updated.netValue = ''; updated.grossValue = ''; }
      } else {
        const nPrice = typeof updated.unitNetPrice === 'number' ? updated.unitNetPrice : 0;
        if (qty > 0 && nPrice > 0) {
          updated.netValue = round2(qty * nPrice);
          updated.grossValue = round2(updated.netValue * (1 + rate / 100));
          updated.unitGrossPrice = round2(nPrice * (1 + rate / 100));
        } else { updated.netValue = ''; updated.grossValue = ''; }
      }
      return updated;
    }));
  };

  // Sumowanie wartości z pozycji do nagłówka. Faktury KSeF mają autorytatywne
  // wartości w nagłówku (P_13_1/P_14_1/P_15) – sumy per-line są tylko widokiem.
  // Dlatego efekt odpala się TYLKO po faktycznej edycji pozycji przez
  // użytkownika (dodanie/usunięcie/zmiana wartości w tabeli). Wczytanie
  // pozycji z KSeF nie zmienia netAmount/grossAmount.
  React.useEffect(() => {
    if (!withLineItems) return;
    if (!userEditedItemsRef.current) return;
    let totalNet = 0;
    let totalGross = 0;
    const filledItems = lineItems.filter(item => typeof item.netValue === 'number' || typeof item.grossValue === 'number');
    lineItems.forEach(item => {
      if (typeof item.netValue === 'number') totalNet += item.netValue;
      if (typeof item.grossValue === 'number') totalGross += item.grossValue;
    });
    const vatRates = filledItems.map(item => item.vatRate);
    const uniqueRates = Array.from(new Set(vatRates));
    const resolvedVatRate = uniqueRates.length === 1 ? uniqueRates[0] : 'różne';
    setForm(p => ({
      ...p,
      netAmount: totalNet > 0 ? round2(totalNet) : '',
      grossAmount: totalGross > 0 ? round2(totalGross) : '',
      vatRate: resolvedVatRate as any,
    }));
  }, [lineItems, withLineItems]);

  // Style pól zgodne z AddProjectForm
  const textFieldSx = {
    '& .MuiOutlinedInput-root': { height: 32, backgroundColor: '#fff', borderRadius: '4px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiInputBase-input': { padding: '8px 12px', boxSizing: 'border-box', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
    '& .MuiFormHelperText-root': { ml: 0 },
    '& .MuiInputLabel-root': { fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '12px', color: '#2D2D32' }
  } as const;

  const selectSx = {
    '& .MuiOutlinedInput-root': { height: 32, backgroundColor: '#fff', borderRadius: '4px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiSelect-select': { padding: '8px 12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' }
  } as const;

  /**
   * Czerwona ramka dla pól obowiązkowych, które nie zostały jeszcze wypełnione.
   * Celowo nie używamy propa `error` MUI, bo `textFieldSx`/`selectSx` mają
   * override'y dla klasy `.Mui-error` (kolor pozostawał szary). Stosujemy
   * własne selectory z `!important`, zachowując grubość 1 px — dzięki temu
   * pole nie zmienia wysokości względem wypełnionych sąsiadów.
   */
  const requiredErrorBorderSx = {
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f !important', borderWidth: '1px !important' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f !important' },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f !important', borderWidth: '1px !important' },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f !important' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f !important', borderWidth: '1px !important' },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f !important', borderWidth: '1px !important' },
    '& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f !important' },
    '& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f !important', borderWidth: '1px !important' },
  } as const;

  const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 0.5 }}>
      {text}
      {required && <Box component="span" sx={{ color: '#d32f2f' }}> *</Box>}
    </Typography>
  );

  const isKsefInvoice = !!invoice.ksef_invoice_id;

  const mapPaymentMethodToDocumentType = (paymentMethod: string | null | undefined): 'Przelew' | 'Proforma' | 'Gotówka' | 'Karta' | 'Zaliczka (przelew)' => {
    if (!paymentMethod) return 'Przelew';
    const method = String(paymentMethod).toLowerCase().trim();
    if (method === 'gotówka' || method === 'gotowka') return 'Gotówka';
    if (method === 'przelew') return 'Przelew';
    if (method === 'karta') return 'Karta';
    if (method === 'zaliczka' || method.includes('zaliczka')) return 'Zaliczka (przelew)';
    return 'Przelew';
  };

  // Skrót klawiszowy "§" do przełączania zakładek
  React.useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === '§') {
        e.preventDefault();
        setFormTab((prev) => (prev === 0 ? 1 : 0));
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open]);

  // Lista użytkowników do dropdownu „Wprowadził" – ładowana raz po otwarciu.
  // Używamy publicznego endpointu z `forBusiness=1` (bez pracowników terenowych),
  // bo dialog jest dostępny dla zwykłych użytkowników, nie tylko adminów.
  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    listUsersPublicForBusinessForms()
      .then((list) => {
        if (cancelled) return;
        setEnteredByUsers(list as any);
        // Jeśli aktualne `enteredByUserId` nie jest na liście (np. user nieaktywny
        // już), a jest dostępna nazwa zachowanego twórcy – nadal pokażemy go
        // w renderValue jako fallback. Listę zostawiamy bez zmian.
      })
      .catch(() => { if (!cancelled) setEnteredByUsers([]); });
    return () => { cancelled = true; };
  }, [open]);

  // Przelicz pozycje przy zmianie trybu obliczeń (netto/brutto)
  React.useEffect(() => {
    setLineItems(prev => prev.map(item => {
      const qty = typeof item.quantity === 'number' ? item.quantity : 0;
      const rate = typeof item.vatRate === 'number' ? item.vatRate : 0;
      if (form.calcMode === 'gross') {
        const nPrice = typeof item.unitNetPrice === 'number' ? item.unitNetPrice : 0;
        if (nPrice > 0) {
          const gPrice = round2(nPrice * (1 + rate / 100));
          return { ...item, unitGrossPrice: gPrice, grossValue: qty > 0 ? round2(qty * gPrice) : '', netValue: qty > 0 ? round2(qty * nPrice) : '' };
        }
      } else {
        const gPrice = typeof item.unitGrossPrice === 'number' ? item.unitGrossPrice : 0;
        if (gPrice > 0 && rate > 0) {
          const nPrice = round2(gPrice / (1 + rate / 100));
          return { ...item, unitNetPrice: nPrice, netValue: qty > 0 ? round2(qty * nPrice) : '', grossValue: qty > 0 ? round2(qty * gPrice) : '' };
        }
      }
      return item;
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.calcMode]);

  // Aktualizuj formularz gdy invoice się zmienia lub dialog się otwiera
  React.useEffect(() => {
    if (!open) return;

    // Reset flag edycji pozycji – wczytujemy świeże dane (z props lub KSeF),
    // efekt sumujący ma się NIE odpalić.
    userEditedItemsRef.current = false;

    let documentKind: DocumentKind = (invoice.document_kind as DocumentKind) || 'vat';
    let paymentMethod: PaymentMethod = (invoice.document_type as PaymentMethod) || 'Przelew';
    if ((invoice as any).invoice_type === 'correction') {
      documentKind = 'correction';
      paymentMethod = 'Przelew';
    } else if (isKsefInvoice && invoice.payment_method) {
      // Dla faktur KSeF sposób płatności ZAWSZE pochodzi z XML (payment_method),
      // a nie z `document_type`, które w liście faktur jest najczęściej
      // ustawiane domyślnie na „Przelew".
      paymentMethod = mapPaymentMethodToDocumentType(invoice.payment_method) as PaymentMethod;
      if (invoice.document_kind) documentKind = invoice.document_kind as DocumentKind;
    } else if (invoice.document_kind) {
      documentKind = invoice.document_kind as DocumentKind;
    } else if (invoice.document_type === 'Zaliczka (przelew)') {
      documentKind = 'advance';
      paymentMethod = 'Zaliczka (przelew)';
    } else if (invoice.document_type === 'Proforma') {
      documentKind = 'proforma';
      paymentMethod = 'Proforma';
    } else if (invoice.document_type && ['Przelew', 'Gotówka', 'Karta'].includes(invoice.document_type)) {
      documentKind = 'vat';
      paymentMethod = invoice.document_type as PaymentMethod;
    }

    let dueDate = invoice.due_date || '';
    if (isKsefInvoice && invoice.payment_method) {
      const paymentMethod = invoice.payment_method.toLowerCase().trim();
      if (paymentMethod === 'przelew' || paymentMethod.includes('zaliczka')) {
        if (invoice.payment_due_date) dueDate = invoice.payment_due_date;
      } else if (paymentMethod === 'gotówka' || paymentMethod === 'gotowka' || paymentMethod === 'karta') {
        if (invoice.document_date) dueDate = invoice.document_date;
      }
    }

    setForm({
      invoiceDate: invoice.document_date || '',
      receivedDate: invoice.received_date || '',
      projectId: invoice.project_id || '',
      invoiceNumber: invoice.invoice_number || '',
      contractorId: invoice.contractor_id ? String(invoice.contractor_id) : undefined,
      contractorName: invoice.contractor_name || '',
      netAmount: Number(invoice.net_amount || 0) as number | '',
      vatRate: (invoice.vat_rate === 'zw' ? 'zw' : (invoice.vat_rate === 'różne' ? 'różne' : Number(invoice.vat_rate))) as 0 | 8 | 23 | 'zw' | 'różne',
      grossAmount: Number(invoice.gross_amount || 0) as number | '',
      dueDate: dueDate,
      priority: invoice.priority || 'zwykły',
      paymentMethod,
      documentKind,
      calcMode: (invoice.calc_mode || 'net') as 'net' | 'gross' | 'none',
      ksefStatus: (invoice.ksef_status === 'Zatwierdzona' || invoice.ksef_status === 'Odrzucona' ? invoice.ksef_status : '') as 'Zatwierdzona' | 'Odrzucona' | '',
      bankAccount: invoice.account_number || '',
      nip: invoice.nip || '',
      contractorAddress: invoice.contractor_address || '',
      contractorPostalCity: invoice.contractor_postal_city || '',
      warehouse: invoice.warehouse || '',
      ksefNumber: invoice.ksef_number || '',
      enteredByUserId: (invoice.created_by_user_id || (user as any)?.user_id || '') as string,
    });

    // Dla faktur KSEF pobieramy PDF i pozycje
    if (isKsefInvoice && invoice.ksef_invoice_id) {
      const ksefId = String(invoice.ksef_invoice_id || '');
      // PDF
      (async () => {
        try {
          if (!ksefId) { setFileUrl(null); return; }
          const response = await api.get(`/ksef/invoices/${encodeURIComponent(ksefId)}/pdf`, { responseType: 'blob' });
          const blob = new Blob([response.data], { type: 'application/pdf' });
          setFileUrl(window.URL.createObjectURL(blob));
        } catch { setFileUrl(null); }
      })();
      // Pozycje faktury z KSeF
      (async () => {
        try {
          setLoadingItems(true);
          const { data } = await api.get(`/ksef/invoices/${encodeURIComponent(ksefId)}`);
          const items = data?.items || [];
          const ksefCalcMode = data?.calcMode || 'net'; // 'net' lub 'gross' z backendu
          if (items.length > 0) {
            const isGross = ksefCalcMode === 'gross';
            setForm(p => ({ ...p, calcMode: isGross ? 'gross' : 'net' }));
            setLineItems(items.map((item: any, idx: number) => {
              const vatR = item.vat_rate != null ? Number(item.vat_rate) : 23;
              const qty = item.quantity ? Number(item.quantity) : 0;
              // Preferuj nowe kolumny unit_price_net/unit_price_gross (spójne z is_netto),
              // a dopiero w razie braku używaj legacy unit_price (stare rekordy sprzed migracji).
              const rawNet = item.unit_price_net != null && item.unit_price_net !== '' ? Number(item.unit_price_net) : null;
              const rawGross = item.unit_price_gross != null && item.unit_price_gross !== '' ? Number(item.unit_price_gross) : null;
              const rawLegacy = item.unit_price != null && item.unit_price !== '' ? Number(item.unit_price) : null;
              const preferredUnit = isGross
                ? (rawGross ?? rawLegacy ?? rawNet)
                : (rawNet ?? rawLegacy ?? rawGross);
              const unitPrice: number | '' = preferredUnit != null && Number.isFinite(preferredUnit) ? preferredUnit : '';
              let unitNetPrice: number | '' = '';
              let unitGrossPrice: number | '' = '';
              let netVal: number | '' = item.net_price != null && item.net_price !== '' ? Number(item.net_price) : '';
              let grossVal: number | '' = item.gross_price != null && item.gross_price !== '' ? Number(item.gross_price) : '';

              if (isGross) {
                // Faktura w cenach brutto: unit_price = cena brutto
                unitGrossPrice = rawGross ?? (typeof unitPrice === 'number' ? unitPrice : '');
                unitNetPrice = rawNet ?? (typeof unitGrossPrice === 'number' && vatR > 0
                  ? round2(unitGrossPrice / (1 + vatR / 100))
                  : (typeof unitGrossPrice === 'number' ? unitGrossPrice : ''));
                if (grossVal === '' && typeof unitGrossPrice === 'number' && qty > 0) {
                  grossVal = round2(unitGrossPrice * qty);
                }
                if (netVal === '' && typeof grossVal === 'number' && grossVal > 0) {
                  netVal = vatR > 0 ? round2(grossVal / (1 + vatR / 100)) : grossVal;
                }
              } else {
                // Faktura w cenach netto: unit_price = cena netto
                unitNetPrice = rawNet ?? (typeof unitPrice === 'number' ? unitPrice : '');
                unitGrossPrice = rawGross ?? (typeof unitNetPrice === 'number' && vatR > 0
                  ? round2(unitNetPrice * (1 + vatR / 100))
                  : (typeof unitNetPrice === 'number' ? unitNetPrice : ''));
                if (netVal === '' && typeof unitNetPrice === 'number' && qty > 0) {
                  netVal = round2(unitNetPrice * qty);
                }
                if (grossVal === '' && typeof netVal === 'number' && netVal > 0) {
                  grossVal = vatR > 0 ? round2(netVal * (1 + vatR / 100)) : netVal;
                }
              }

              return {
                id: item.id || idx + 1,
                name: item.name || '',
                quantity: qty > 0 ? qty : '' as number | '',
                unit: normalizeUnit(item.unit),
                unitNetPrice: unitNetPrice,
                unitGrossPrice: unitGrossPrice,
                vatRate: vatR,
                netValue: netVal,
                grossValue: grossVal,
              };
            }));
            setWithLineItems(true);
          } else {
            setLineItems([emptyLineItem()]);
          }
        } catch {
          setLineItems([emptyLineItem()]);
        } finally {
          setLoadingItems(false);
        }
      })();
    } else {
      // Wyciągnij Google Drive fileId z URL-a i użyj API backendu (natywny PDF z zaznaczaniem tekstu)
      const driveUrl = String(invoice.google_drive_file_url || '')
        || (invoice.google_drive_file_id ? `https://drive.google.com/file/d/${invoice.google_drive_file_id}/preview` : '');
      const m1 = driveUrl.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
      const m2 = driveUrl.match(/id=([a-zA-Z0-9_-]+)/);
      const driveFileId = (m1 && m1[1]) || (m2 && m2[1]) || invoice.google_drive_file_id || '';
      const apiBase = (process.env.REACT_APP_API_URL || '');
      const pdfSrc = driveFileId
        ? `${apiBase}/kzms/attachments/${encodeURIComponent(driveFileId)}/content`
        : (invoice.id ? `${apiBase}/transfer-invoices/${invoice.id}/pdf` : null);
      setFileUrl(pdfSrc);
      // Ładuj pozycje z bazy danych
      if (invoice.id) {
        (async () => {
          try {
            setLoadingItems(true);
            const { getInvoiceItems } = await import('../../services/invoicesService');
            const items = await getInvoiceItems(invoice.id);
            if (items.length > 0) {
              setLineItems(items.map((item: any, idx: number) => {
                const unitNet = item.unit_net_price != null ? Number(item.unit_net_price) : '';
                const unitGross = item.unit_gross_price != null ? Number(item.unit_gross_price) : '';
                return {
                  id: item.id || idx + 1,
                  name: item.name || '',
                  quantity: item.quantity != null ? Number(item.quantity) : '',
                  unit: normalizeUnit(item.unit),
                  unitNetPrice: unitNet,
                  unitGrossPrice: unitGross,
                  vatRate: item.vat_rate != null ? (isNaN(Number(item.vat_rate)) ? item.vat_rate : Number(item.vat_rate)) : 23,
                  netValue: item.net_value != null ? Number(item.net_value) : '',
                  grossValue: item.gross_value != null ? Number(item.gross_value) : '',
                };
              }));
              setWithLineItems(true);
            } else {
              setLineItems([emptyLineItem()]);
            }
          } catch {
            setLineItems([emptyLineItem()]);
          } finally {
            setLoadingItems(false);
          }
        })();
      } else {
        setLineItems([emptyLineItem()]);
      }
    }
    setFile(null);
    setContractorOptions([]);
    setLegalAcceptance(true);
    // Inicjalizacja statusu białej listy z istniejącej faktury
    const wlStatus = (invoice as any).white_list_status;
    if (wlStatus === 'valid' || wlStatus === 'invalid') {
      setBankAccountCheckStatus(wlStatus);
      setBankAccountCheckedAt((invoice as any).white_list_checked_at || null);
    } else {
      setBankAccountCheckStatus('idle');
      setBankAccountCheckedAt(null);
    }
    setFormTab(0);
  }, [open, invoice, isKsefInvoice]);

  // Cleanup blob URL
  React.useEffect(() => {
    return () => { if (fileUrl && fileUrl.startsWith('blob:')) window.URL.revokeObjectURL(fileUrl); };
  }, [fileUrl]);

  // Sync contractorSearchQuery
  React.useEffect(() => {
    if (!open) return;
    setContractorSearchQuery(form.contractorName || '');
  }, [open, form.contractorName]);

  // Load projects
  React.useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setLoadingProjects(true);
        const all = await getAllProjects();
        const active = all.filter((p) => p.project_phase === 'W REALIZACJI').map((p) => ({ project_id: p.project_id, project_name: p.project_name }));
        setProjects([...active, ...STATIC_PROJECTS]);
      } finally { setLoadingProjects(false); }
    })();
    // Rachunki bankowe Unitresco nie są już ładowane tutaj — pole rachunku dotyczy kontrahenta
  }, [open]);

  // ZASADA: dane KSeF wchodzą do formularza DOSŁOWNIE.
  //
  // Nie ma żadnego automatycznego efektu, który po inicjalizacji "poprawia"
  // netto/brutto/VAT. Dla faktur KSeF wartości z FA(3) (P_13_1, P_14_1, P_15)
  // są autorytatywne i muszą być pokazane co do grosza – nawet jeśli
  // suma_per_line różni się od nagłówka (per-line rounding wpisany w schemat).
  //
  // Przeliczanie net⇄brutto odpala się TYLKO wtedy, gdy użytkownik realnie
  // edytuje pole (handlery `recalcOnNetEdit` / `recalcOnGrossEdit` /
  // `recalcOnVatRateEdit` poniżej). Inicjalizacja z prop `invoice`,
  // wczytanie pozycji z KSeF czy włączenie/wyłączenie trybu „z pozycjami"
  // nigdy nie zmieniają wartości netto/brutto.
  const recalcOnNetEdit = (
    prev: typeof form,
    nextNet: number | '',
  ): Partial<typeof form> => {
    const updates: any = { netAmount: nextNet };
    if (prev.vatRate === 'różne' || !prev.calcMode || prev.calcMode === 'gross') return updates;
    const vatPercent = prev.vatRate === 'zw' ? 0 : Number(prev.vatRate);
    if (typeof nextNet === 'number' && nextNet > 0) {
      updates.grossAmount = round2(nextNet * (1 + vatPercent / 100));
    } else if (nextNet === '') {
      updates.grossAmount = '';
    }
    return updates;
  };
  const recalcOnGrossEdit = (
    prev: typeof form,
    nextGross: number | '',
  ): Partial<typeof form> => {
    const updates: any = { grossAmount: nextGross };
    if (prev.vatRate === 'różne' || !prev.calcMode || prev.calcMode === 'net') return updates;
    const vatPercent = prev.vatRate === 'zw' ? 0 : Number(prev.vatRate);
    if (typeof nextGross === 'number' && nextGross > 0) {
      updates.netAmount = vatPercent === 0 ? nextGross : round2(nextGross / (1 + vatPercent / 100));
    } else if (nextGross === '') {
      updates.netAmount = '';
    }
    return updates;
  };
  const recalcOnVatRateEdit = (
    prev: typeof form,
    nextRate: 0 | 8 | 23 | 'zw' | 'różne',
  ): Partial<typeof form> => {
    const updates: any = {
      vatRate: nextRate,
      calcMode:
        nextRate === 'różne'
          ? 'none'
          : prev.calcMode === 'none' || !prev.calcMode
            ? 'net'
            : prev.calcMode,
    };
    if (nextRate === 'różne') return updates;
    const vatPercent = nextRate === 'zw' ? 0 : Number(nextRate);
    if (updates.calcMode === 'net' && typeof prev.netAmount === 'number' && prev.netAmount > 0) {
      updates.grossAmount = round2(prev.netAmount * (1 + vatPercent / 100));
    } else if (updates.calcMode === 'gross' && typeof prev.grossAmount === 'number' && prev.grossAmount > 0) {
      updates.netAmount = vatPercent === 0 ? prev.grossAmount : round2(prev.grossAmount / (1 + vatPercent / 100));
    }
    return updates;
  };

  // Auto-fill due date for cash/card
  React.useEffect(() => {
    if ((form.paymentMethod === 'Gotówka' || form.paymentMethod === 'Karta') && form.invoiceDate && form.invoiceDate !== form.dueDate) {
      setForm((prev) => ({ ...prev, dueDate: form.invoiceDate }));
    }
  }, [form.paymentMethod, form.invoiceDate]);

  /**
   * Rozpoznaje strukturę identyfikatora projektu, od którego zależy zawartość
   * pola „Magazyn":
   *  - `NNN-MMM` (np. `224-225`) → projekt dualny: user wybiera jeden z dwóch
   *    podkodów (pierwszy = Materiały, drugi = Usługi). W bazie zapisujemy
   *    wyłącznie sam kod (`224` lub `225`).
   *  - `SNN` (np. `S14`) → magazyn techniczny; pole wypełniamy automatycznie
   *    całym `project_id` i blokujemy przed edycją.
   *  - cokolwiek innego → brak wymuszeń (np. niestandardowe projekty).
   */
  const projectMeta = React.useMemo(() => {
    const id = String(form.projectId || '').trim();
    if (!id) return { kind: 'none' as const };
    const dual = id.match(/^(\d+)\s*-\s*(\d+)$/);
    if (dual) {
      return {
        kind: 'dual' as const,
        codes: [
          { value: dual[1], label: `${dual[1]} - Materiały` },
          { value: dual[2], label: `${dual[2]} - Usługi` },
        ],
      };
    }
    if (/^S\d+/i.test(id)) {
      return { kind: 'tech' as const, code: id.toUpperCase() };
    }
    return { kind: 'other' as const };
  }, [form.projectId]);

  // Synchronizacja pola `warehouse` z wybranym projektem.
  React.useEffect(() => {
    if (projectMeta.kind === 'tech') {
      if (form.warehouse !== projectMeta.code) {
        setForm((prev) => ({ ...prev, warehouse: projectMeta.code }));
      }
      return;
    }
    if (projectMeta.kind === 'dual') {
      const allowed = projectMeta.codes.map((c) => c.value);
      if (form.warehouse && !allowed.includes(form.warehouse)) {
        setForm((prev) => ({ ...prev, warehouse: '' }));
      }
      return;
    }
    if (projectMeta.kind === 'none' && form.warehouse) {
      setForm((prev) => ({ ...prev, warehouse: '' }));
    }
  }, [projectMeta, form.warehouse]);

  const searchContractors = React.useCallback(async (query: string) => {
    if (!query.trim() || query.length < 2) { setContractorOptions([]); return; }
    try {
      setLoadingContractors(true);
      const response = await contractorService.filterContractors({ searchText: query }, 1, 10);
      setContractorOptions(response.contractors);
    } catch { setContractorOptions([]); } finally { setLoadingContractors(false); }
  }, []);

  // Wyszukiwanie kontrahentów po NIP — wyniki trafiają do contractorOptions
  const nipSearchTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchContractorsByNip = React.useCallback((nipValue: string) => {
    if (nipSearchTimer.current) clearTimeout(nipSearchTimer.current);
    const digits = nipValue.replace(/[^0-9]/g, '');
    if (digits.length < 2) return;
    nipSearchTimer.current = setTimeout(async () => {
      try {
        setLoadingContractors(true);
        const response = await contractorService.filterContractors({ nipText: digits }, 1, 10);
        setContractorOptions(response.contractors);
      } catch { setContractorOptions([]); } finally { setLoadingContractors(false); }
    }, 300);
  }, []);

  const onDropFile = (f: File) => {
    if (f && f.type === 'application/pdf') {
      if (fileUrl && fileUrl.startsWith('blob:')) URL.revokeObjectURL(fileUrl);
      setFileUrl(URL.createObjectURL(f));
      setFile(f);
    }
  };

  // Oświadczenie musi być domyślnie odznaczone — user świadomie je zaznacza.
  const [legalAcceptance, setLegalAcceptance] = React.useState(false);
  const [bankAccountCheckStatus, setBankAccountCheckStatus] = React.useState<'idle' | 'checking' | 'valid' | 'invalid' | 'error'>('idle');
  const [bankAccountCheckedAt, setBankAccountCheckedAt] = React.useState<string | null>(null);

  const validateNrbChecksum = (nrb: string): boolean => {
    if (nrb.length !== 26 || !/^\d{26}$/.test(nrb)) return false;
    const reordered = nrb.slice(2) + '2521' + nrb.slice(0, 2);
    let remainder = 0;
    for (let i = 0; i < reordered.length; i++) { remainder = (remainder * 10 + parseInt(reordered[i], 10)) % 97; }
    return remainder === 1;
  };

  // Ujednolicone sprawdzanie białej listy. Wariant `silent` (domyślny dla
  // auto-checków) NIE wyświetla globalnych powiadomień – feedback dostarcza
  // inline napis pod polem rachunku.
  const runBankAccountCheck = async (silent: boolean = false) => {
    const cleanAccount = (form.bankAccount || '').replace(/[\s-]/g, '');
    if (!cleanAccount) return;
    let nip = form.nip || '';
    if (!nip && form.contractorId) {
      try {
        const contractor = await contractorService.getContractorById(form.contractorId);
        if (contractor?.nip) { nip = contractor.nip; setForm((p) => ({ ...p, nip })); }
      } catch {}
    }
    const cleanNip = (nip || '').replace(/[\s-]/g, '');
    if (!cleanNip) { if (!silent) showNotification('Brak NIP kontrahenta.', 'warning'); return; }
    if (cleanAccount.length !== 26 || !/^\d{26}$/.test(cleanAccount)) { if (!silent) showNotification('Numer rachunku bankowego musi mieć dokładnie 26 cyfr.', 'warning'); return; }
    if (!validateNrbChecksum(cleanAccount)) { setBankAccountCheckStatus('error'); if (!silent) showNotification('Nieprawidłowa suma kontrolna numeru rachunku bankowego.', 'warning'); return; }
    if (cleanNip.length !== 10 || !/^\d{10}$/.test(cleanNip)) { if (!silent) showNotification('NIP kontrahenta musi mieć dokładnie 10 cyfr.', 'warning'); return; }
    setBankAccountCheckStatus('checking');
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await api.get('/white-list/check', { params: { nip: cleanNip, bankAccount: cleanAccount, date: today } });
      const data = response.data;
      const checkedAt = new Date().toISOString();
      const assigned = data.result?.accountAssigned;
      if (data.success && assigned === 'TAK') {
        setBankAccountCheckStatus('valid'); setBankAccountCheckedAt(checkedAt);
        if (!silent) showNotification('Rachunek figuruje na białej liście VAT.', 'success');
      } else if (data.success && assigned === 'VIRTUAL_MATCH') {
        setBankAccountCheckStatus('valid'); setBankAccountCheckedAt(checkedAt);
        if (!silent) {
          const base = data.result?.matchingBaseAccount || '';
          showNotification(`Rachunek wirtualny — dopasowany${base ? ` do bazowego ${base.slice(0,2)} ${base.slice(2,6)} ${base.slice(6,10)}...` : ''}.`, 'success');
        }
      } else if (data.success && assigned === 'PROBABLE_MATCH') {
        setBankAccountCheckStatus('invalid'); setBankAccountCheckedAt(checkedAt);
        if (!silent) showNotification('Rachunek wirtualny — dopasowano tylko bank/oddział (8 cyfr). Wymaga potwierdzenia u kontrahenta.', 'warning');
      } else if (data.success && assigned === 'FOREIGN_ACCOUNT') {
        setBankAccountCheckStatus('valid'); setBankAccountCheckedAt(checkedAt);
        if (!silent) showNotification('Rachunek zagraniczny — biała lista VAT nie obejmuje kont spoza Polski.', 'info');
      } else if (data.success && (assigned === 'NIE' || assigned === 'VIRTUAL_NO_MATCH')) {
        setBankAccountCheckStatus('invalid'); setBankAccountCheckedAt(checkedAt);
        if (!silent) showNotification('Rachunek NIE figuruje na białej liście VAT!', 'warning');
      } else {
        setBankAccountCheckStatus('invalid'); setBankAccountCheckedAt(checkedAt);
        if (!silent) showNotification(data.message || 'Nie można zweryfikować rachunku.', 'warning');
      }
    } catch (error: any) {
      setBankAccountCheckStatus('error'); setBankAccountCheckedAt(null);
      if (!silent) showNotification(error?.response?.data?.message || 'Nie udało się zweryfikować rachunku.', 'error');
    }
  };

  const checkBankAccount = () => runBankAccountCheck(false);

  // Auto-sprawdzanie białej listy: jeśli mamy poprawny NRB i NIP, a status
  // jeszcze nie został wyznaczony, odpalamy `runBankAccountCheck(true)`
  // z debouncem 500 ms. Toasty są wyciszone – feedback w postaci inline
  // (zielony/czerwony) napisu obok pola.
  React.useEffect(() => {
    if (!open) return;
    if (bankAccountCheckStatus !== 'idle') return;
    const cleanAccount = (form.bankAccount || '').replace(/[\s-]/g, '');
    if (cleanAccount.length !== 26 || !/^\d{26}$/.test(cleanAccount)) return;
    if (!validateNrbChecksum(cleanAccount)) return;
    const cleanNip = (form.nip || '').replace(/[\s-]/g, '');
    if (cleanNip.length !== 10 || !/^\d{10}$/.test(cleanNip)) return;
    const t = setTimeout(() => { void runBankAccountCheck(true); }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.bankAccount, form.nip, bankAccountCheckStatus]);

  const [saving, setSaving] = React.useState(false);
  const canSave = isKsefInvoice
    ? (!!form.projectId && !!form.warehouse && (form.ksefStatus === 'Zatwierdzona' || form.ksefStatus === 'Odrzucona') && legalAcceptance)
    : (!!form.invoiceDate && !!form.receivedDate && !!form.projectId && !!form.warehouse && !!form.contractorName && typeof form.netAmount === 'number' && form.netAmount > 0 && !!form.dueDate && legalAcceptance);

  const handleClose = () => {
    setFile(null);
    setFileUrl(null);
    setContractorSearchQuery('');
    setContractorOptions([]);
    setLegalAcceptance(true);
    if (window.opener) {
      window.close();
    } else {
      onClose();
    }
  };

  const handleSave = async () => {
    if (saving) return;
    if (!canSave) {
      if (isKsefInvoice) {
        showNotification('Uzupełnij projekt, magazyn, wybierz status (Zatwierdzona/Odrzucona) i zaakceptuj oświadczenie.', 'warning');
      } else {
        showNotification('Wypełnij wszystkie wymagane pola (data, projekt, magazyn, kontrahent, kwoty, termin, oświadczenie) przed zapisem.', 'warning');
      }
      return;
    }

    try {
      setSaving(true);

      if (isKsefInvoice && invoice.ksef_invoice_id) {
        const ksefId = String(invoice.ksef_invoice_id || '');
        if (!ksefId) throw new Error('Brak ID faktury KSEF');

        if (form.ksefStatus === 'Zatwierdzona') {
          if (!form.invoiceDate || !form.receivedDate || !form.projectId || !form.contractorName || typeof form.netAmount !== 'number' || form.netAmount <= 0 || !form.dueDate) {
            showNotification('Wypełnij wszystkie wymagane pola przed zatwierdzeniem.', 'error');
            setSaving(false);
            return;
          }

          showNotification('Trwa przesyłanie PDF do Google Drive i zapisywanie w bazie...', 'info');

          let pdfFile: File | null = null;
          try {
            const pdfResponse = await api.get(`/ksef/invoices/${encodeURIComponent(ksefId)}/pdf`, { responseType: 'blob' });
            pdfFile = new File([new Blob([pdfResponse.data], { type: 'application/pdf' })], `ksef-${ksefId}.pdf`, { type: 'application/pdf' });
          } catch {
            showNotification('Nie udało się pobrać PDF z KSEF. Spróbuj ponownie.', 'error');
            setSaving(false);
            return;
          }

          const itemsPayload = lineItems.filter(it => it.name || (typeof it.quantity === 'number' && it.quantity > 0) || (typeof it.unitNetPrice === 'number' && it.unitNetPrice > 0)).map(it => ({
            name: it.name, quantity: it.quantity, unit: it.unit, unitNetPrice: it.unitNetPrice, unitGrossPrice: it.unitGrossPrice, vatRate: it.vatRate, netValue: it.netValue, grossValue: it.grossValue,
          }));

          await createTransferInvoice({
            document_type: form.paymentMethod,
            document_kind: form.documentKind,
            document_date: form.invoiceDate,
            received_date: form.receivedDate,
            project_id: form.projectId,
            contractor_id: form.contractorId ? String(form.contractorId) : undefined,
            contractor_name: form.contractorName,
            account_number: form.bankAccount || undefined,
            nip: form.nip || undefined,
            contractor_address: form.contractorAddress || undefined,
            contractor_postal_city: form.contractorPostalCity || undefined,
            warehouse: form.warehouse || undefined,
            ksef_number: form.ksefNumber || undefined,
            white_list_status: bankAccountCheckStatus === 'valid' || bankAccountCheckStatus === 'invalid' ? bankAccountCheckStatus : undefined,
            white_list_checked_at: bankAccountCheckedAt || undefined,
            net_amount: typeof form.netAmount === 'number' ? form.netAmount : 0,
            vat_rate: (form.vatRate === 'zw' ? 'zw' : (form.vatRate === 'różne' ? 'różne' : String(form.vatRate))) as any,
            gross_amount: typeof form.grossAmount === 'number' ? form.grossAmount : 0,
            calc_mode: form.calcMode || 'net',
            due_date: form.dueDate,
            priority: form.priority,
            invoice_number: form.invoiceNumber || undefined,
            comment: undefined,
            created_by_user_id: form.enteredByUserId || undefined,
            pdf: pdfFile,
            items: itemsPayload,
          } as any, (user as any)?.google_access_token);

          await api.patch(`/ksef/invoices/${encodeURIComponent(ksefId)}/status`, { status: form.ksefStatus });
          showNotification('Faktura została zapisana i wysłana do akceptacji.', 'success');
        } else {
          await api.patch(`/ksef/invoices/${encodeURIComponent(ksefId)}/status`, { status: form.ksefStatus });
          showNotification(form.ksefStatus === 'Odrzucona' ? 'Status faktury został zaktualizowany na "Odrzucona".' : 'Status faktury został zaktualizowany.', 'success');
        }
      } else {
        showNotification('Zapisywanie zmian faktury...', 'info');
        const payload: any = {
          document_type: form.paymentMethod,
          document_kind: form.documentKind,
          document_date: form.invoiceDate,
          received_date: form.receivedDate,
          project_id: form.projectId,
          invoice_number: form.invoiceNumber,
          contractor_id: form.contractorId,
          contractor_name: form.contractorName,
          account_number: form.bankAccount || undefined,
          nip: form.nip || undefined,
          contractor_address: form.contractorAddress || undefined,
          contractor_postal_city: form.contractorPostalCity || undefined,
          warehouse: form.warehouse || undefined,
          ksef_number: form.ksefNumber || undefined,
          white_list_status: bankAccountCheckStatus === 'valid' || bankAccountCheckStatus === 'invalid' ? bankAccountCheckStatus : undefined,
          white_list_checked_at: bankAccountCheckedAt || undefined,
          net_amount: typeof form.netAmount === 'number' ? form.netAmount : 0,
          vat_rate: form.vatRate === 'zw' ? 'zw' : (form.vatRate === 'różne' ? 'różne' : String(form.vatRate)),
          gross_amount: typeof form.grossAmount === 'number' ? form.grossAmount : 0,
          calc_mode: form.calcMode || 'net',
          due_date: form.dueDate,
          priority: form.priority,
          created_by_user_id: form.enteredByUserId || undefined,
          items: lineItems.filter(it => it.name || (typeof it.quantity === 'number' && it.quantity > 0) || (typeof it.unitNetPrice === 'number' && it.unitNetPrice > 0)).map(it => ({
            name: it.name, quantity: it.quantity, unit: it.unit, unitNetPrice: it.unitNetPrice, unitGrossPrice: it.unitGrossPrice, vatRate: it.vatRate, netValue: it.netValue, grossValue: it.grossValue,
          })),
        };
        if (file) payload.pdf = file;
        await updateTransferInvoice(invoice.id, payload);
        showNotification('Zaktualizowano fakturę.', 'success');
      }

      // Powiadom okno nadrzędne
      try {
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({ type: 'invoice:updated' }, '*');
        }
      } catch {}

      onSaved?.();
      handleClose();
    } catch (e: any) {
      console.error('Nie udało się zaktualizować faktury:', e);
      showNotification(e?.response?.data?.error || e?.response?.data?.message || 'Nie udało się zaktualizować faktury.', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę fakturę?')) return;
    try {
      const { deleteTransferInvoice } = await import('../../services/invoicesService');
      await deleteTransferInvoice(invoice.id);
      showNotification('Faktura została usunięta.', 'success');
      try { if (window.opener && !window.opener.closed) window.opener.postMessage({ type: 'invoice:updated' }, '*'); } catch {}
      onSaved?.();
      handleClose();
    } catch {
      showNotification('Nie udało się usunąć faktury.', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullScreen
      PaperProps={{ sx: { bgcolor: '#FAFAFA' } }}>
      {/* Górny pasek */}
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #EDEDED', px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, minHeight: 56 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
          <Tooltip title={user?.display_name || `${user?.first_name || ''} ${user?.last_name || ''}` || user?.email || 'Użytkownik'}>
            <Avatar src={user?.avatar_url || undefined} sx={{ width: 28, height: 28, flexShrink: 0 }}>
              {getInitials(user?.display_name || `${user?.first_name || ''} ${user?.last_name || ''}` || user?.email || 'U')}
            </Avatar>
          </Tooltip>
          <KeyboardArrowRight sx={{ color: 'text.secondary', flexShrink: 0 }} />
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#020242', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {isKsefInvoice && invoice.ksef_invoice_id
              ? `KSeF: ${invoice.ksef_invoice_id}`
              : 'Edytuj fakturę'}
          </Typography>
        </Box>
      </Box>

      {/* Główna zawartość – podział 50/50 */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', p: 2, gap: 2 }}>
        {/* Lewa kolumna – podgląd PDF (50%) */}
        <Box sx={{ flex: '0 0 50%', minWidth: 0 }}>
          <Box
            sx={{
              border: '2px dashed #d4d4d9',
              borderRadius: '4px',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              bgcolor: fileUrl ? '#fff' : 'transparent',
            }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) onDropFile(f);
            }}
          >
            {!fileUrl ? (
              <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                <PictureAsPdf sx={{ fontSize: 56, color: 'text.disabled' }} />
                <Typography sx={{ mt: 1, fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#595966' }}>Przeciągnij i upuść plik PDF tutaj</Typography>
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: '#B9B9B9' }}>lub</Typography>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1 }}>
                  <Button variant="outlined" component="label" size="small"
                    sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '13px', borderColor: '#d4d4d9', color: '#2D2D32' }}>
                    Wybierz plik PDF
                    <input type="file" hidden accept="application/pdf" onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onDropFile(f);
                    }} />
                  </Button>
                </Box>
              </Box>
            ) : (
              <iframe title="invoice-preview-edit" src={fileUrl} style={{ width: '100%', height: '100%', border: 0 }} />
            )}
          </Box>
        </Box>

          {/* Prawa kolumna – formularz z zakładkami (50%) */}
          <Box sx={{ flex: '0 0 50%', display: 'flex', flexDirection: 'column', bgcolor: '#fff', borderRadius: '4px', border: '1px solid #EDEDED', overflow: 'hidden' }}>
            {/* Zakładki */}
            <Box sx={{ borderBottom: '1px solid #EDEDED', px: 2 }}>
              <Tabs
                value={formTab}
                onChange={(_, v) => setFormTab(v)}
                variant="standard"
                sx={{
                  minHeight: 36,
                  '& .MuiTabs-indicator': { height: 2, bgcolor: '#000000' },
                  '& .MuiTabs-flexContainer': { gap: 0 }
                }}
              >
                <Tab disableRipple label="Informacje"
                  sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, color: '#020242', minHeight: 36, px: 2, py: 0, '&.Mui-selected': { fontWeight: 500, color: '#020242' } }} />
                <Tab disableRipple label="Wartości i pozycje"
                  sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, color: '#020242', minHeight: 36, px: 2, py: 0, '&.Mui-selected': { fontWeight: 500, color: '#020242' } }} />
              </Tabs>
            </Box>

            {/* Karta: Informacje */}
            <Box sx={{ flex: 1, overflowY: 'auto', pt: 2, px: 2, display: formTab === 0 ? 'block' : 'none' }}>
              <Grid container rowSpacing={1.5} columnSpacing={2}>
                <Grid item xs={8.4}>
                  <FieldLabel text="Projekt" required />
                  <Autocomplete
                    size="small"
                    options={projects}
                    loading={loadingProjects}
                    getOptionLabel={(p) => `${p.project_id} ${p.project_name}`}
                    value={projects.find((p) => p.project_id === form.projectId) || null}
                    onChange={(_, val) => setForm((prev) => ({ ...prev, projectId: val ? String(val.project_id) : '' }))}
                    isOptionEqualToValue={(a, b) => a.project_id === b.project_id}
                    ListboxProps={{ sx: { '& .MuiAutocomplete-option': { fontSize: '12px', py: 0.5, minHeight: 28 } } }}
                    sx={{
                      '& .MuiAutocomplete-inputRoot': { padding: '0 9px !important', height: 32 },
                      '& .MuiAutocomplete-input': { padding: '0 !important' },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Wybierz projekt"
                        sx={{
                          ...textFieldSx,
                          ...(!form.projectId ? requiredErrorBorderSx : {}),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={3.6}>
                  <FieldLabel text="Magazyn" required />
                  {projectMeta.kind === 'dual' ? (
                    <FormControl
                      fullWidth size="small"
                      sx={{
                        ...selectSx,
                        ...(!form.warehouse ? requiredErrorBorderSx : {}),
                      }}
                    >
                      <Select
                        displayEmpty
                        value={form.warehouse || ''}
                        onChange={(e) => setForm((p) => ({ ...p, warehouse: String(e.target.value || '') }))}
                        renderValue={(v) => (v ? String(v) : <span style={{ color: '#9e9e9e' }}>000</span>)}
                      >
                        {projectMeta.codes.map((c) => (
                          <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth size="small"
                      value={form.warehouse || ''}
                      placeholder={projectMeta.kind === 'none' ? 'Wybierz projekt' : '000'}
                      disabled={projectMeta.kind === 'tech' || projectMeta.kind === 'none'}
                      onChange={(e) => { const v = e.target.value.slice(0, 3).toUpperCase(); setForm((p) => ({ ...p, warehouse: v })); }}
                      inputProps={{ maxLength: 3 }}
                      sx={{
                        ...textFieldSx,
                        ...(((!form.warehouse && projectMeta.kind !== 'tech') || highlightWarehouse) ? requiredErrorBorderSx : {}),
                      }}
                      helperText={highlightWarehouse ? 'Uzupełnij magazyn, aby dodać asortyment' : ''}
                    />
                  )}
                </Grid>
                <Grid item xs={6}>
                  <FieldLabel text="Data dokumentu" required />
                  <TextField fullWidth type="date" value={form.invoiceDate}
                    onChange={(e) => setForm((p) => ({ ...p, invoiceDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }} size="small" sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FieldLabel text="Data otrzymania" required />
                  <TextField fullWidth type="date" value={form.receivedDate}
                    onChange={(e) => setForm((p) => ({ ...p, receivedDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }} size="small" sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={7.8}>
                  <FieldLabel text="Nr KSeF" />
                  <TextField fullWidth value={form.ksefNumber || ''}
                    onChange={(e) => setForm((p) => ({ ...p, ksefNumber: e.target.value }))}
                    size="small" sx={textFieldSx} placeholder="Nr KSeF"
                  />
                </Grid>
                <Grid item xs={4.2}>
                  <FieldLabel text="Nr Faktury" />
                  <TextField fullWidth value={form.invoiceNumber}
                    onChange={(e) => setForm((p) => ({ ...p, invoiceNumber: e.target.value }))}
                    size="small" sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={7.8}>
                  <FieldLabel text="Kontrahent" required />
                  <Autocomplete
                    size="small" freeSolo options={contractorOptions} loading={loadingContractors}
                    inputValue={contractorSearchQuery}
                    onInputChange={(_, newValue, reason) => {
                      setContractorSearchQuery(newValue);
                      if (reason === 'input') {
                        searchContractors(newValue);
                      }
                    }}
                    value={null}
                    onChange={(_, newValue) => {
                      if (typeof newValue === 'string') {
                        setForm((prev) => ({ ...prev, contractorId: undefined, contractorName: newValue.trim(), nip: '', contractorAddress: '', contractorPostalCity: '' }));
                        setContractorSearchQuery(newValue.trim());
                      } else if (newValue && typeof newValue === 'object' && 'id' in newValue) {
                        const c = newValue as Contractor;
                        const nipVal = c.nip || '';
                        setForm((prev) => ({
                          ...prev,
                          contractorId: c.id,
                          contractorName: c.name,
                          nip: nipVal,
                          contractorAddress: c.address || '',
                          contractorPostalCity: c.location || '',
                        }));
                        setContractorSearchQuery(c.name);
                      } else {
                        setForm((prev) => ({ ...prev, contractorId: undefined, contractorName: '', nip: '', contractorAddress: '', contractorPostalCity: '' }));
                        setContractorSearchQuery('');
                      }
                      setContractorOptions([]);
                    }}
                    onBlur={() => {
                      const trimmed = contractorSearchQuery.trim();
                      if (trimmed) {
                        setForm((prev) => ({ ...prev, contractorName: trimmed }));
                      } else if (!form.contractorId) {
                        setForm((prev) => ({ ...prev, contractorName: '', contractorId: undefined }));
                      }
                    }}
                    getOptionLabel={(option) => typeof option === 'string' ? option : (option && 'name' in option ? option.name : '')}
                    isOptionEqualToValue={(option, value) => {
                      if (typeof option === 'string' && typeof value === 'string') return (option as string).trim() === (value as string).trim();
                      if (typeof option === 'object' && typeof value === 'object' && option !== null && value !== null && 'id' in option && 'id' in value) return String((option as Contractor).id) === String((value as Contractor).id);
                      if (typeof option === 'string' && typeof value === 'object' && value !== null && 'name' in value) return (option as string).trim() === (value as Contractor).name.trim();
                      if (typeof option === 'object' && typeof value === 'string' && option !== null && 'name' in option) return (option as Contractor).name.trim() === (value as string).trim();
                      return false;
                    }}
                    filterOptions={(x) => x}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Wpisz nazwę kontrahenta..." sx={textFieldSx}
                        InputProps={{ ...params.InputProps, endAdornment: (<>{loadingContractors ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>) }}
                      />
                    )}
                    renderOption={(props, option) => typeof option === 'string' ? (
                      <Box component="li" {...props} key={option}><Typography variant="body2">{option}</Typography></Box>
                    ) : (
                      <Box component="li" {...props} key={option.id}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                          <Typography variant="body2">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.location ? `${option.location}` : ''}{option.location && option.nip ? ', ' : ''}{option.nip ? `NIP: ${option.nip}` : ''}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    noOptionsText={contractorSearchQuery.length < 2 ? 'Wpisz co najmniej 2 znaki' : 'Brak wyników. Możesz wprowadzić kontrahenta spoza bazy.'}
                  />
                </Grid>
                <Grid item xs={4.2}>
                  <FieldLabel text="NIP" />
                  <TextField fullWidth size="small" value={form.nip || ''} placeholder="NIP kontrahenta"
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm((p) => ({ ...p, nip: val }));
                      searchContractorsByNip(val);
                    }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={7.8}>
                  <FieldLabel text="Adres (ulica i numer)" />
                  <TextField fullWidth size="small" value={form.contractorAddress || ''} placeholder="np. ul. Gałczyńskiego 4"
                    onChange={(e) => setForm((p) => ({ ...p, contractorAddress: e.target.value }))}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={4.2}>
                  <FieldLabel text="Kod pocztowy i miejscowość" />
                  <TextField fullWidth size="small" value={form.contractorPostalCity || ''} placeholder="np. 00-362 Warszawa"
                    onChange={(e) => setForm((p) => ({ ...p, contractorPostalCity: e.target.value }))}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel text="Nr rachunku bankowego" />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Pole rachunku ma stałą szerokość dopasowaną do
                        formatu „XX XXXX XXXX XXXX XXXX XXXX XXXX" – 26 cyfr
                        + 6 spacji ≈ 32 znaki. 360 px to wygodna szerokość
                        dla typowego kroju monospace + padding MUI. */}
                    <TextField
                      size="small"
                      value={form.bankAccount || ''}
                      onChange={(e) => {
                        setForm((p) => ({ ...p, bankAccount: e.target.value }));
                        setBankAccountCheckStatus('idle');
                        setBankAccountCheckedAt(null);
                      }}
                      placeholder="00 0000 0000 0000 0000 0000 0000"
                      sx={{ ...textFieldSx, width: 360, flexShrink: 0 }}
                    />
                    <Tooltip title={
                      bankAccountCheckStatus === 'valid' ? `Rachunek figuruje na białej liście VAT${bankAccountCheckedAt ? ' (' + new Date(bankAccountCheckedAt).toLocaleDateString('pl-PL') + ')' : ''}`
                        : bankAccountCheckStatus === 'invalid' ? `Rachunek NIE figuruje na białej liście VAT${bankAccountCheckedAt ? ' (' + new Date(bankAccountCheckedAt).toLocaleDateString('pl-PL') + ')' : ''}`
                        : bankAccountCheckStatus === 'checking' ? 'Sprawdzanie...'
                        : bankAccountCheckStatus === 'error' ? 'Błąd weryfikacji – kliknij ponownie'
                        : !form.nip && !form.contractorId ? 'Wybierz kontrahenta z bazy (z NIP) aby sprawdzić'
                        : !(form.bankAccount || '').replace(/[\s-]/g, '') ? 'Wpisz numer rachunku'
                        : 'Sprawdź na białej liście VAT (API MF)'
                    }>
                      <span>
                        <IconButton
                          size="small"
                          onClick={checkBankAccount}
                          disabled={!(form.bankAccount || '').replace(/[\s-]/g, '') || (!form.nip && !form.contractorId) || bankAccountCheckStatus === 'checking'}
                          sx={{
                            border: '1px solid',
                            borderColor: bankAccountCheckStatus === 'valid' ? '#4caf50' : bankAccountCheckStatus === 'invalid' ? '#f44336' : '#d4d4d9',
                            borderRadius: '4px', width: 32, height: 32,
                            bgcolor: bankAccountCheckStatus === 'valid' ? '#e8f5e9' : bankAccountCheckStatus === 'invalid' ? '#ffebee' : 'transparent',
                            '&:hover': { borderColor: bankAccountCheckStatus === 'valid' ? '#388e3c' : bankAccountCheckStatus === 'invalid' ? '#d32f2f' : '#595966',
                              bgcolor: bankAccountCheckStatus === 'valid' ? '#c8e6c9' : bankAccountCheckStatus === 'invalid' ? '#ffcdd2' : '#f5f5f5' },
                          }}
                        >
                          {bankAccountCheckStatus === 'checking' ? (
                            <CircularProgress size={16} />
                          ) : bankAccountCheckStatus === 'valid' ? (
                            <CheckCircle sx={{ fontSize: 20, color: '#4caf50' }} />
                          ) : bankAccountCheckStatus === 'invalid' ? (
                            <CancelIcon sx={{ fontSize: 20, color: '#f44336' }} />
                          ) : (
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: bankAccountCheckStatus === 'error' ? '#f44336' : '#595966', lineHeight: 1 }}>WL</Typography>
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    {/* Inline status białej listy – aktualizowany automatycznie
                        po wczytaniu danych lub edycji NRB/NIP. */}
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'Roboto, sans-serif',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        color:
                          bankAccountCheckStatus === 'valid' ? '#2e7d32'
                            : bankAccountCheckStatus === 'invalid' ? '#d32f2f'
                            : bankAccountCheckStatus === 'error' ? '#d32f2f'
                            : '#9e9e9e',
                      }}
                    >
                      {bankAccountCheckStatus === 'valid' ? 'Na białej liście VAT'
                        : bankAccountCheckStatus === 'invalid' ? 'Brak na białej liście VAT'
                        : bankAccountCheckStatus === 'checking' ? 'Sprawdzanie…'
                        : bankAccountCheckStatus === 'error' ? 'Błąd weryfikacji'
                        : ''}
                      {(bankAccountCheckStatus === 'valid' || bankAccountCheckStatus === 'invalid') && bankAccountCheckedAt
                        ? ` (${new Date(bankAccountCheckedAt).toLocaleDateString('pl-PL')})`
                        : ''}
                    </Typography>
                  </Box>
                </Grid>
                {/* Wprowadził – domyślnie aktualny user, można zmienić.
                    Backend zapisuje `created_by_user_id`; w tabeli (kolumna „W")
                    JOIN po users.user_id pokazuje awatar + display_name. */}
                <Grid item xs={6}>
                  <FieldLabel text="Wprowadził" />
                  <FormControl size="small" fullWidth sx={selectSx}>
                    <Select
                      value={form.enteredByUserId || ''}
                      displayEmpty
                      onChange={(e) => setForm((p) => ({ ...p, enteredByUserId: String(e.target.value) }))}
                      renderValue={(value) => {
                        const u = enteredByUsers.find((x) => x.user_id === value);
                        // Fallback: jeśli aktualnego twórcy nie ma na liście (np.
                        // konto nieaktywne), korzystamy z danych z faktury, żeby
                        // nie pokazać pustego pola.
                        const fallbackName = invoice.created_by_name || (user as any)?.display_name;
                        const fallbackAvatar = invoice.created_by_avatar_url || (user as any)?.avatar_url;
                        if (!value) {
                          return <Box component="span" sx={{ color: '#9e9e9e' }}>Wybierz użytkownika</Box>;
                        }
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar src={u?.avatar_url || fallbackAvatar || undefined} sx={{ width: 22, height: 22, fontSize: 12 }}>
                              {(u?.display_name || fallbackName || '?').substring(0, 1)}
                            </Avatar>
                            <Box component="span">{u?.display_name || fallbackName || value}</Box>
                          </Box>
                        );
                      }}
                    >
                      {enteredByUsers.map((u) => (
                        <MenuItem key={u.user_id} value={u.user_id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar src={u.avatar_url || undefined} sx={{ width: 22, height: 22, fontSize: 12 }}>
                              {(u.display_name || u.email || '?').substring(0, 1)}
                            </Avatar>
                            <Box component="span">{u.display_name || u.email}</Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} />
                <Grid item xs={6}>
                  <FieldLabel text="Termin płatności" required />
                  <TextField fullWidth type="date" value={form.dueDate}
                    onChange={(e) => setForm((p) => ({ ...p, dueDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }} size="small" sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FieldLabel text="Status" />
                  <FormControl size="small" fullWidth sx={selectSx}>
                    <Select value={form.priority}
                      onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value as 'zwykły' | 'pilny' }))}>
                      <MenuItem value={'zwykły'}>zwykły</MenuItem>
                      <MenuItem value={'pilny'}>pilny</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={isKsefInvoice ? 6 : 12}>
                  <FieldLabel text="Rodzaj" />
                  <FormControl size="small" fullWidth sx={selectSx}>
                    <Select value={form.documentKind}
                      onChange={(e) => setForm((p) => ({ ...p, documentKind: e.target.value as DocumentKind }))}
                      displayEmpty renderValue={(v) => (v ? ({ vat: 'Faktura VAT', advance: 'Zaliczka', proforma: 'Proforma', correction: 'Korekta' } as Record<string, string>)[v] || String(v) : 'Rodzaj')}>
                      <MenuItem value={'vat'}>Faktura VAT</MenuItem>
                      <MenuItem value={'advance'}>Zaliczka</MenuItem>
                      <MenuItem value={'proforma'}>Proforma</MenuItem>
                      <MenuItem value={'correction'}>Korekta</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={isKsefInvoice ? 6 : 12}>
                  <FieldLabel text="Sposób płatności" />
                  <FormControl size="small" fullWidth sx={selectSx}>
                    <Select value={form.paymentMethod}
                      onChange={(e) => setForm((p) => ({ ...p, paymentMethod: e.target.value as PaymentMethod }))}
                      displayEmpty renderValue={(v) => (v ? `${v}` : 'Sposób płatności')}>
                      <MenuItem value={'Przelew'}>Przelew</MenuItem>
                      <MenuItem value={'Proforma'}>Proforma</MenuItem>
                      <MenuItem value={'Gotówka'}>Gotówka</MenuItem>
                      <MenuItem value={'Karta'}>Karta</MenuItem>
                      <MenuItem value={'Zaliczka (przelew)'}>Zaliczka (przelew)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            {/* Karta: Wartości i pozycje */}
            <Box sx={{ flex: 1, display: formTab === 1 ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Przełącznik z pozycjami / bez pozycji */}
              <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d4d4d9' }}>
                  <Button size="small" onClick={() => setWithLineItems(false)}
                    sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '12px', fontWeight: 500, borderRadius: 0, px: 1.5, py: 0.4,
                      backgroundColor: !withLineItems ? '#4040DB' : '#fff', color: !withLineItems ? '#fff' : '#595966',
                      '&:hover': { backgroundColor: !withLineItems ? '#1F1FB0' : '#f5f5f5' }, minWidth: 0 }}>
                    Bez pozycji
                  </Button>
                  <Button size="small" onClick={() => setWithLineItems(true)}
                    sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '12px', fontWeight: 500, borderRadius: 0, px: 1.5, py: 0.4, borderLeft: '1px solid #d4d4d9',
                      backgroundColor: withLineItems ? '#4040DB' : '#fff', color: withLineItems ? '#fff' : '#595966',
                      '&:hover': { backgroundColor: withLineItems ? '#1F1FB0' : '#f5f5f5' }, minWidth: 0 }}>
                    Z pozycjami
                  </Button>
                </Box>
                <FormControl size="small" sx={{ minWidth: 150, ml: 'auto', ...selectSx, '& .MuiOutlinedInput-root': { height: 30 }, '& .MuiSelect-select': { fontSize: '12px', py: '4px' } }}>
                  <Select value={form.calcMode === 'none' || !form.calcMode ? 'net' : form.calcMode}
                    onChange={(e) => {
                      const newValue = e.target.value as 'net' | 'gross';
                      if (!withLineItems && form.vatRate === 'różne') return;
                      // Sama zmiana trybu nie modyfikuje wartości – decyduje
                      // tylko, które pole jest read-only. Wartości netto/brutto
                      // zachowujemy 1:1 (ważne dla danych z KSeF).
                      setForm((p) => ({ ...p, calcMode: newValue }));
                    }}
                    disabled={!withLineItems && form.vatRate === 'różne'}>
                    <MenuItem value="net">Licz od netto</MenuItem>
                    <MenuItem value="gross">Licz od brutto</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Pozycje faktury – scrollowalny obszar (tylko gdy z pozycjami) */}
              {withLineItems && (
              <Box sx={{ flex: 1, overflowY: 'auto', px: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 500, color: '#020242' }}>
                    Pozycje faktury {loadingItems && <CircularProgress size={14} sx={{ ml: 1 }} />}
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { px: 0.5, py: 0.5, fontFamily: 'Roboto, sans-serif', fontSize: '12px', borderColor: '#EDEDED' } }}>
                    <TableHead>
                      <TableRow sx={{ '& .MuiTableCell-head': { fontWeight: 500, color: '#595966', fontSize: '11px', borderBottom: '1px solid #d4d4d9', whiteSpace: 'nowrap' } }}>
                        <TableCell sx={{ width: 28 }}>L.p</TableCell>
                        <TableCell>Nazwa</TableCell>
                        <TableCell sx={{ width: 50 }} align="right">Ilość</TableCell>
                        <TableCell sx={{ width: 65 }} align="center">Jm</TableCell>
                        <TableCell sx={{ width: 80 }} align="right">{form.calcMode === 'gross' ? 'Cena brutto' : 'Cena netto'}</TableCell>
                        <TableCell sx={{ width: 70 }} align="center">VAT(%)</TableCell>
                        <TableCell sx={{ width: 80 }} align="right">{form.calcMode === 'gross' ? 'W. brutto' : 'W. netto'}</TableCell>
                        <TableCell sx={{ width: 28 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lineItems.map((item, idx) => (
                        <TableRow key={item.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                          <TableCell sx={{ color: '#595966' }}>{idx + 1}</TableCell>
                          <TableCell>
                            <TextField fullWidth size="small" value={item.name} placeholder="Nazwa"
                              onChange={(e) => updateLineItem(item.id, 'name', e.target.value)}
                              sx={{ ...textFieldSx, '& .MuiOutlinedInput-root': { height: 28, backgroundColor: '#fff', borderRadius: '4px' }, '& .MuiInputBase-input': { padding: '4px 8px', fontSize: '12px' } }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" type="number" value={item.quantity} placeholder="1"
                              onChange={(e) => updateLineItem(item.id, 'quantity', e.target.value === '' ? '' : Number(e.target.value))}
                              inputProps={{ step: '1', min: 0 }}
                              sx={{ ...textFieldSx, '& .MuiOutlinedInput-root': { height: 28, backgroundColor: '#fff', borderRadius: '4px' }, '& .MuiInputBase-input': { padding: '4px 6px', fontSize: '12px', textAlign: 'right' }, width: 50 }}
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl size="small" sx={{ width: 65, ...selectSx, '& .MuiOutlinedInput-root': { height: 28, backgroundColor: '#fff', borderRadius: '4px' }, '& .MuiSelect-select': { padding: '4px 8px', fontSize: '12px' } }}>
                              <Select value={item.unit} onChange={(e) => updateLineItem(item.id, 'unit', e.target.value)}>
                                {ensureUnitOptions(item.unit).map((u) => (
                                  <MenuItem key={u} value={u}>{u}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            {form.calcMode === 'gross' ? (
                              <TextField size="small" type="number" value={fmtPrice(item.unitGrossPrice, `line-${item.id}-ugp`)} placeholder="0.00"
                                onChange={(e) => updateLineItem(item.id, 'unitGrossPrice', e.target.value === '' ? '' : Number(e.target.value))}
                                onFocus={() => setFocusedPriceField(`line-${item.id}-ugp`)}
                                onBlur={() => setFocusedPriceField(null)}
                                inputProps={{ step: '0.01', min: 0 }}
                                sx={{ ...textFieldSx, '& .MuiOutlinedInput-root': { height: 28, backgroundColor: '#fff', borderRadius: '4px' }, '& .MuiInputBase-input': { padding: '4px 6px', fontSize: '12px', textAlign: 'right' }, width: 80 }}
                              />
                            ) : (
                              <TextField size="small" type="number" value={fmtPrice(item.unitNetPrice, `line-${item.id}-unp`)} placeholder="0.00"
                                onChange={(e) => updateLineItem(item.id, 'unitNetPrice', e.target.value === '' ? '' : Number(e.target.value))}
                                onFocus={() => setFocusedPriceField(`line-${item.id}-unp`)}
                                onBlur={() => setFocusedPriceField(null)}
                                inputProps={{ step: '0.01', min: 0 }}
                                sx={{ ...textFieldSx, '& .MuiOutlinedInput-root': { height: 28, backgroundColor: '#fff', borderRadius: '4px' }, '& .MuiInputBase-input': { padding: '4px 6px', fontSize: '12px', textAlign: 'right' }, width: 80 }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <FormControl size="small" sx={{ width: 70, ...selectSx, '& .MuiOutlinedInput-root': { height: 28, backgroundColor: '#fff', borderRadius: '4px' }, '& .MuiSelect-select': { padding: '4px 8px', fontSize: '12px' } }}>
                              <Select value={item.vatRate} onChange={(e) => updateLineItem(item.id, 'vatRate', e.target.value)}>
                                <MenuItem value={23}>23%</MenuItem>
                                <MenuItem value={8}>8%</MenuItem>
                                <MenuItem value={0}>0%</MenuItem>
                                <MenuItem value={'zw'}>zw.</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#2D2D32' }}>
                              {form.calcMode === 'gross'
                                ? (typeof item.grossValue === 'number' ? item.grossValue.toFixed(2) : '—')
                                : (typeof item.netValue === 'number' ? item.netValue.toFixed(2) : '—')
                              }
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => removeLineItem(item.id)} disabled={lineItems.length <= 1}
                              sx={{ p: 0.25, color: '#B40003', '&.Mui-disabled': { color: '#d4d4d9' } }}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" size="small" onClick={addLineItem}
                    sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '13px', fontWeight: 500, borderRadius: '4px', backgroundColor: '#4040DB', color: '#fff', '&:hover': { backgroundColor: '#1F1FB0' }, '&:active': { backgroundColor: '#1A1A98' }, px: 1.5, py: 0.5 }}>
                    Dodaj pozycję
                  </Button>
                </Box>
              </Box>
              )}

              {/* Podsumowanie wartości – stałe na dole */}
              <Box sx={{ flexShrink: 0, borderTop: '1px solid #EDEDED', px: 2, pt: 2, pb: 2, bgcolor: '#fff' }}>
              <Grid container rowSpacing={1.5} columnSpacing={2}>
                <Grid item xs={6}>
                  <FieldLabel text="Wartość netto" required />
                  <TextField
                    fullWidth type="number" value={fmtPrice(form.netAmount, 'netAmount')}
                    onChange={(e) => {
                      if (withLineItems) return;
                      if (form.vatRate !== 'różne' && form.calcMode === 'gross') return;
                      const v = e.target.value === '' ? '' : Number(e.target.value);
                      setForm((p) => ({ ...p, ...recalcOnNetEdit(p, v) }));
                    }}
                    onFocus={() => setFocusedPriceField('netAmount')}
                    onBlur={() => setFocusedPriceField(null)}
                    size="small" inputProps={{ step: '0.01', min: 0 }} sx={textFieldSx}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
                      readOnly: withLineItems || (form.vatRate !== 'różne' && form.calcMode === 'gross')
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FieldLabel text="Stawka VAT" />
                  <FormControl size="small" fullWidth sx={selectSx}>
                    <Select value={form.vatRate}
                      onChange={(e) => {
                        const v = e.target.value as any;
                        setForm((p) => ({ ...p, ...recalcOnVatRateEdit(p, v) }));
                      }}
                      disabled={withLineItems}>
                      <MenuItem value={23}>23%</MenuItem>
                      <MenuItem value={8}>8%</MenuItem>
                      <MenuItem value={0}>0%</MenuItem>
                      <MenuItem value={'zw'}>zw.</MenuItem>
                      <MenuItem value={'różne'}>różne</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FieldLabel text="Wartość VAT" />
                  <TextField
                    fullWidth type="number"
                    value={(() => {
                      const net = typeof form.netAmount === 'number' ? form.netAmount : 0;
                      const gross = typeof form.grossAmount === 'number' ? form.grossAmount : 0;
                      const vat = round2(gross - net);
                      return vat > 0 ? vat.toFixed(2) : '';
                    })()}
                    size="small" inputProps={{ step: '0.01', min: 0 }} sx={textFieldSx}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
                      readOnly: true
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FieldLabel text="Wartość brutto" />
                  <TextField
                    fullWidth type="number" value={fmtPrice(form.grossAmount, 'grossAmount')}
                    onChange={(e) => {
                      if (withLineItems) return;
                      if (form.vatRate !== 'różne' && form.calcMode === 'net') return;
                      const v = e.target.value === '' ? '' : Number(e.target.value);
                      setForm((p) => ({ ...p, ...recalcOnGrossEdit(p, v) }));
                    }}
                    onFocus={() => setFocusedPriceField('grossAmount')}
                    onBlur={() => setFocusedPriceField(null)}
                    size="small" inputProps={{ step: '0.01', min: 0 }} sx={textFieldSx}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
                      readOnly: withLineItems || (form.vatRate !== 'różne' && form.calcMode === 'net')
                    }}
                  />
                </Grid>
                {isKsefInvoice && (
                  <Grid item xs={12}>
                    <FieldLabel text="Status KSeF" required />
                    <FormControl
                      size="small" fullWidth
                      sx={{
                        ...selectSx,
                        ...(!form.ksefStatus ? requiredErrorBorderSx : {}),
                      }}
                    >
                      <Select
                        displayEmpty
                        value={form.ksefStatus}
                        onChange={(e) => setForm((p) => ({ ...p, ksefStatus: e.target.value as any }))}
                        renderValue={(v) => (v ? String(v) : <span style={{ color: '#9e9e9e' }}>Wybierz status</span>)}
                      >
                        <MenuItem value={'Zatwierdzona'}>Zatwierdzona</MenuItem>
                        <MenuItem value={'Odrzucona'}>Odrzucona</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start', m: 0, mt: 1 }}
                    control={
                      <Checkbox checked={legalAcceptance}
                        onChange={(e) => setLegalAcceptance(e.target.checked)}
                        sx={{ mt: 0, p: 0.5 }} size="small"
                      />
                    }
                    label={
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966', textAlign: 'justify', lineHeight: 1.4, ml: 0.5 }}>
                        Potwierdzam, że zgłaszana faktura przelewowa jest bezpośrednio związana z realizacją wybranego projektu i spełnia wymogi kwalifikowalności wydatków określone w OWU. Przyjmuję odpowiedzialność za rzetelność tego oświadczenia zgodnie z § 2.3.1 OWU.
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>
              </Box>
            </Box>

          </Box>
        </Box>

      {/* Dolny pasek z przyciskami */}
      <Box sx={{ bgcolor: '#fff', borderTop: '1px solid #EDEDED', px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexShrink: 0, gap: 1 }}>
        {!isKsefInvoice && (
          <Button onClick={handleDelete} startIcon={<DeleteOutline />} color="error" size="small"
            sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '14px', whiteSpace: 'nowrap', mr: 'auto' }}>
            Usuń
          </Button>
        )}
        <Button onClick={handleClose} disabled={saving}
          sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#595966', whiteSpace: 'nowrap' }}>
          Anuluj
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !canSave}
          sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 500, backgroundColor: '#020242', '&:hover': { backgroundColor: '#030360' }, whiteSpace: 'nowrap' }}>
          {saving ? 'Zapisywanie...' : 'Zapisz'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default EditTransferInvoiceDialog;
